import * as Sentry from '@sentry/react';
import { configureStore } from '@reduxjs/toolkit';
import { crashReporter, logger } from 'Douleutaras/redux/middleware';
import { authSlice } from 'Douleutaras/redux/data/auth/authSlice';
import meSlice from 'Douleutaras/redux/data/users/meSlice';

import reducer from './reducers';

// createReduxEnhancer relies on @sentry/react (browser SDK) being initialised.
// On the SSR server there is no browser SDK, so we skip it entirely.
const sentryReduxEnhancer = typeof window !== 'undefined'
  ? Sentry.createReduxEnhancer({})
  : undefined;

export const initializeStore = initialState => {
  return configureStore({
    reducer,
    preloadedState: initialState,
    devTools: process.env.NODE_ENV !== 'production',
    enhancers: sentryReduxEnhancer ? [sentryReduxEnhancer] : [],
    middleware: getDefaultMiddleware => getDefaultMiddleware({
      serializableCheck: false
    })
      .concat([
        logger
        // after this function handler, the middleware chain will end.
        // So if you need to add more middlewares, you should place it above.
        // crashReporter
      ])
  });
};

// On the client, hydrate from the state the SSR server serialised into the page.
// On the server this global is never set, so we fall back to a blank slate.
const preloadedState = typeof window !== 'undefined'
  ? (window as Window & { _PRELOADED_STATE?: object })._PRELOADED_STATE
  : undefined;

export const store = initializeStore(preloadedState ?? { auth: { user: {} } });
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// For routes whose handle.anonymized is true (see dataRoutes.tsx) — the SSR response is cached
// at the Cloudflare edge and must render identically for every visitor, so user-specific slices
// are reset to their default shape before the state gets serialized into the HTML. This is a
// belt-and-suspenders check: the primary fix is never fetching this data for those routes in the
// first place (see src/server/index.tsx) — this guards against a future addition to the render
// path re-introducing personalized data another way.
export const stripPersonalizedState = (state: RootState): RootState => ({
  ...state,
  auth: authSlice.getInitialState(),
  me: meSlice.getInitialState()
});
